import { useState, useEffect, useRef } from 'preact/hooks';
import { api } from '../api/client';
import { SkeletonBar, Button } from '../components/ui';

import { T } from '../styles/tokens';
const FONT = "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace";

function timeAgo(dateStr: string): string {
  const s = Math.round((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

function statusIcon(status: string, conclusion: string | null) {
  if (status === 'in_progress') return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: T.warning, animation: 'pulse-dot 1.5s infinite' }} />;
  if (conclusion === 'success') return <span style={{ color: T.success }}></span>;
 if (conclusion === 'failure') return <span style={{ color: T.danger }}></span>;
  if (conclusion === 'cancelled') return <span style={{ color: T.textMuted }}>○</span>;
  return <span style={{ color: T.textMuted }}>?</span>;
}

export function AdminGithubActions() {
  const [runs, setRuns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [jobs, setJobs] = useState<Record<number, any[]>>({});
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef<any>(null);

  const fetchRuns = async () => {
    try {
      const data = await api.get<any>('/admin/github-actions/runs');
      setRuns(data.runs || []);
    } catch { }
    setLoading(false);
  };

  useEffect(() => { fetchRuns(); }, []);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchRuns, 30000);
      return () => clearInterval(intervalRef.current);
    } else {
      clearInterval(intervalRef.current);
    }
  }, [autoRefresh]);

  const toggleExpand = async (runId: number) => {
    if (expanded === runId) { setExpanded(null); return; }
    setExpanded(runId);
    if (!jobs[runId]) {
      try {
        const data = await api.get<any>(`/admin/github-actions/runs/${runId}/jobs`);
        setJobs(prev => ({ ...prev, [runId]: data.jobs || [] }));
      } catch { }
    }
  };

  return (
    <div style={{ height: '100%', overflow: 'auto', background: T.bg, padding: 20, fontFamily: FONT }}>
      <style>{`@keyframes pulse-dot { 0%,100% { opacity: .4 } 50% { opacity: 1 } } @keyframes skel { 0%,100% { opacity: .3 } 50% { opacity: 1 } }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
 <span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>GitHub Actions</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div onClick={() => setAutoRefresh(!autoRefresh)} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: T.textMuted }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: autoRefresh ? T.success : T.textMuted, animation: autoRefresh ? 'pulse-dot 1.5s infinite' : 'none' }} />
            {autoRefresh ? 'Auto-refresh 30s' : 'Pausado'}
          </div>
          <Button variant="secondary" size="sm" onClick={() => { setLoading(true); fetchRuns(); }}>Refresh</Button>
        </div>
      </div>

      {/* Runs list */}
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
        {loading ? (
          [0,1,2,3].map(i => <div key={i} style={{ height: 44, background: T.bgCard, animation: 'skel 1.5s infinite', borderBottom: `1px solid ${T.borderSubtle}` }} />)
        ) : runs.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
 <div style={{ fontSize: 40, marginBottom: 8 }}></div>
            <div style={{ color: T.textMuted, fontSize: 13 }}>Nenhum workflow run encontrado</div>
          </div>
        ) : (
          runs.map(r => (
            <div key={r.id}>
              <div
                onClick={() => toggleExpand(r.id)}
                style={{ height: 44, padding: '0 16px', display: 'flex', alignItems: 'center', borderBottom: `1px solid ${T.borderSubtle}`, cursor: 'pointer', transition: 'background 150ms ease', gap: 0 }}
                onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgSubtle}
                onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ width: 32, textAlign: 'center', fontSize: 14 }}>{statusIcon(r.status, r.conclusion)}</span>
                <span style={{ width: 60, color: T.textMuted, fontSize: 12 }}>#{r.runNumber}</span>
                <span style={{ flex: 1, color: T.text, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(r.commitMsg || '').substring(0, 50)}</span>
                <span style={{ background: T.primaryBg, color: T.primary, fontSize: 11, padding: '2px 8px', borderRadius: 4, marginRight: 12 }}>{r.branch}</span>
                <span style={{ width: 80, color: T.textMuted, fontSize: 12 }}>{formatDuration(r.duration)}</span>
                <span style={{ width: 100, textAlign: 'right', color: T.textMuted, fontSize: 12 }}>{timeAgo(r.createdAt)}</span>
              </div>

              {expanded === r.id && (
                <div style={{ background: T.bg, padding: 16, borderTop: `1px solid ${T.borderSubtle}` }}>
                  {!jobs[r.id] ? (
                    <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>
                  ) : jobs[r.id].length === 0 ? (
                    <div style={{ color: T.textMuted, fontSize: 12 }}>Nenhum job encontrado</div>
                  ) : (
                    jobs[r.id].map((j: any) => (
                      <div key={j.id} style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.text }}>
                          {statusIcon(j.status, j.conclusion)}
                          <span style={{ fontWeight: 600 }}>{j.name}</span>
                          <span style={{ color: T.textMuted }}>{formatDuration(j.duration)}</span>
                        </div>
                        {(j.steps || []).map((s: any, i: number) => (
                          <div key={i} style={{ paddingLeft: 20, fontSize: 11, color: T.textMuted, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
 <span>{s.conclusion === 'success' ? '' : s.conclusion === 'failure' ? '' : s.conclusion === 'skipped' ? '–' : '○'}</span>
                            <span>{s.name}</span>
                          </div>
                        ))}
                      </div>
                    ))
                  )}
                  <a href={r.url} target="_blank" rel="noopener" style={{ color: T.primary, fontSize: 12, textDecoration: 'none' }} onMouseEnter={(e: any) => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={(e: any) => e.currentTarget.style.textDecoration = 'none'}>Abrir no GitHub </a>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
